import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCriminalWizardContext } from '../../../hooks/useCaseRequestWizard';
import type { CriminalWizardDraft } from '../../../types/criminalCaseRequest';
import { isWizardCategorizationEditable } from '../../../utils/wizardCategoryRoute';
import type { RequiredErrors } from '../../../utils/requiredValidation';
import { StepBar, StepSep, WizardStep } from '../WizardComponents';
import { wizardStepBarLabel, wizardStepTitle } from '../wizardI18nHelpers';
import { CriminalCaseCategorizationCard } from './CriminalCaseCategorizationCard';
import { useRole } from '../../../rbac/hooks';
import { tasksListPathForRole } from '../../../rbac/tasksListPath';

const STEPS = [{ n: 1 }, { n: 2 }, { n: 3 }, { n: 4 }, { n: 5 }] as const;

export function criminalWizardPath(step: (typeof STEPS)[number]['n'], caseRequestId?: string): string {
  const base = `/requests/new/step-${String(step)}`;
  const id = (caseRequestId ?? '').trim();
  return id ? `${base}/${id}` : base;
}

export function CriminalWizardShell({
  currentStep,
  draft,
  setDraft,
  categorizationEditable = true,
  categorizationErrors,
  onClearCategorizationError,
  children,
}: {
  currentStep: 1 | 2 | 3 | 4 | 5;
  draft: CriminalWizardDraft;
  setDraft?: React.Dispatch<React.SetStateAction<CriminalWizardDraft>>;
  categorizationEditable?: boolean;
  categorizationErrors?: RequiredErrors;
  onClearCategorizationError?: (key: string) => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { roleCode } = useRole();
  const { setDraft: contextSetDraft } = useCriminalWizardContext();
  const effectiveSetDraft = setDraft ?? contextSetDraft;
  const showWizard = Boolean(draft.fileCategoryId);

  return (
    <>
      <div className="bc">
        <Link to={tasksListPathForRole(roleCode)}>{t('nav.tasksAndCases')}</Link>
        <span className="bc-sep">/</span>
        {t('wizard.createCaseRequest')}
      </div>

      <div className="pg-title">{t('wizard.createCaseRequest')}</div>
      <div className="pg-sub" style={{ marginBottom: 16 }}>
        {t('wizard.createSubtitle')}
      </div>

      <CriminalCaseCategorizationCard
        draft={draft}
        setDraft={effectiveSetDraft}
        editable={isWizardCategorizationEditable(categorizationEditable, draft.draftRequestId)}
        errors={categorizationErrors}
        onClearError={onClearCategorizationError}
      />

      {showWizard ? (
        <>
          <StepBar>
            {STEPS.map((s, idx) => {
              const state = currentStep > s.n ? 'done' : currentStep === s.n ? 'active' : 'pending';
              return (
                <div key={s.n} style={{ display: 'flex', alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
                  <WizardStep
                    state={state}
                    numberOrCheck={state === 'done' ? '✓' : String(s.n)}
                    stepLabel={wizardStepBarLabel(t, s.n)}
                    name={wizardStepTitle(t, 'criminal', s.n)}
                    clickable={false}
                  />
                  {idx < STEPS.length - 1 ? <StepSep /> : null}
                </div>
              );
            })}
          </StepBar>

          {children}
        </>
      ) : null}
    </>
  );
}

export function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 6, fontWeight: 600 }} role="alert">
      {message}
    </div>
  );
}

export function inpClass(hasError: boolean): string {
  return `ccr-inp${hasError ? ' inv' : ''}`;
}
